/**
 * @fileoverview HorizontalLayoutRenderer component for the Storyteller web application.
 * Renders a horizontal layout with configurable spacing and alignment.
 *
 * @example
 * // Basic usage
 * <HorizontalLayoutRenderer schema={schema} uischema={uischema} />
 */

import { HorizontalLayout, RankedTester, rankWith, RendererProps, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsLayoutProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import React, { FunctionComponent } from 'react'
import css from './HorizontalLayoutRenderer.module.css'
import { JsonFormsLayout } from './JsonFormsLayout'
import { renderChildren } from './utils'

/**
 * Default tester for a horizontal layout.
 * @type {RankedTester}
 */
export const horizontalLayoutTester: RankedTester = rankWith(1, uiTypeIs('HorizontalLayout'))

/**
 * @function HorizontalLayoutRenderer
 * @description Component for rendering a horizontal layout.
 * @param {RendererProps & VanillaRendererProps} props - The props for the HorizontalLayoutRenderer component.
 * @returns {JSX.Element} The HorizontalLayoutRenderer component.
 */
export const HorizontalLayoutRenderer = (props: RendererProps & VanillaRendererProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data, ...otherProps } = props
    // We don't hand over data to the layout renderer to avoid rerendering it with every data change
    return <HorizontalLayoutRendererComponent {...otherProps} />
}

/**
 * @function HorizontalLayoutRendererComponent
 * @description Component for rendering a horizontal layout.
 * @param {RendererProps & VanillaRendererProps} props - The props for the HorizontalLayoutRendererComponent component.
 * @returns {JSX.Element} The HorizontalLayoutRendererComponent component.
 */
const HorizontalLayoutRendererComponent: FunctionComponent<RendererProps & VanillaRendererProps> =
    React.memo(function HorizontalLayoutRendererComponent({
        schema,
        uischema,
        getStyle,
        getStyleAsClassName,
        enabled,
        visible,
        path,
    }: RendererProps & VanillaRendererProps) {
        const horizontalLayout = uischema as HorizontalLayout

        const customGap = uischema.options?.padding
        const style: Record<string, unknown> = {}

        if (customGap) {
            style.padding = customGap
        }

        return (
            <div className={css.horizontalLayout} style={style}>
                <JsonFormsLayout
                    className={css.horizontalLayout}
                    visible={visible}
                    enabled={enabled}
                    path={path}
                    uischema={uischema}
                    schema={schema}
                    getStyle={getStyle}
                    getStyleAsClassName={getStyleAsClassName}
                >
                    {renderChildren(
                        horizontalLayout,
                        schema,
                        css.horizontalLayout_item,
                        path,
                        enabled
                    )}
                </JsonFormsLayout>
            </div>
        )
    })

export default withVanillaControlProps(withJsonFormsLayoutProps(HorizontalLayoutRenderer, false))
